import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  getDoc,
  getDocs,
  updateDoc, 
  deleteDoc,
  arrayUnion, 
  arrayRemove,
  increment,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { parseMentions, validateMentions } from '../utils/mentionUtils';
import { createMentionNotification } from './notificationService';

export const createPost = async (text, user, channelId) => {
  try {
    // Parse mentions from the text
    const mentionedUsernames = parseMentions(text);
    console.log('postService: Detected mentions:', mentionedUsernames);
    
    // Create the post
    const docRef = await addDoc(collection(db, 'posts'), {
      text: text.trim(),
      authorId: user.uid,
      authorEmail: user.email,
      channelId: channelId,
      createdAt: serverTimestamp(),
      likesCount: 0,
      likedByUsers: [],
      repliesCount: 0,
      mentionedUsernames: mentionedUsernames
    });
    
    // Create notifications for mentioned users
    if (mentionedUsernames.length > 0) {
      try {
        const { validMentions } = await validateMentions(mentionedUsernames);
        console.log('postService: Valid mentions:', validMentions);
        
        // Create notification for each valid mentioned user
        const notificationPromises = validMentions
          .filter(mentionedUser => mentionedUser.email !== user.email) // Don't notify yourself
          .map(mentionedUser => {
            console.log('postService: Creating notification for:', mentionedUser.email);
            return createMentionNotification(
              mentionedUser.email,
              user,
              'post',
              docRef.id,
              channelId
            );
          });
        
        await Promise.all(notificationPromises);
        console.log('postService: All notifications created');
      } catch (notificationError) {
        console.error('Error creating mention notifications:', notificationError);
        // Don't fail the post creation if notifications fail
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

export const subscribeToPosts = (callback, channelId = null) => {
  let q;
  if (channelId) {
    q = query(
      collection(db, 'posts'), 
      where('channelId', '==', channelId),
      orderBy('createdAt', 'desc')
    );
  } else {
    q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
  }
  
  return onSnapshot(q, (querySnapshot) => {
    const posts = [];
    querySnapshot.forEach((doc) => {
      posts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(posts);
  });
};

export const toggleLike = async (postId, user) => {
  try {
    const postRef = doc(db, 'posts', postId);
    
    // First, get the current post to check if user already liked it
    const postDoc = await getDoc(postRef);
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    const likedByUsers = postData.likedByUsers || [];
    const isLiked = likedByUsers.some(likedUser => likedUser.id === user.uid);
    
    const userLikeData = {
      id: user.uid,
      email: user.email
    };
    
    if (isLiked) {
      // Unlike the post - remove the user object
      const updatedLikedBy = likedByUsers.filter(likedUser => likedUser.id !== user.uid);
      await updateDoc(postRef, {
        likedByUsers: updatedLikedBy,
        likesCount: increment(-1)
      });
    } else {
      // Like the post - add the user object  
      await updateDoc(postRef, {
        likedByUsers: arrayUnion(userLikeData),
        likesCount: increment(1)
      });
    }
    
    return !isLiked; // Return new like status
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};

export const deletePost = async (postId, userId) => {
  try {
    // First check if the user is the author of the post
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    if (postData.authorId !== userId) {
      throw new Error('You can only delete your own posts');
    }
    
    // Delete all replies (cascading delete)
    const repliesRef = collection(db, 'posts', postId, 'replies');
    const repliesSnapshot = await getDocs(repliesRef);
    
    const deletePromises = [];
    repliesSnapshot.forEach((replyDoc) => {
      deletePromises.push(deleteDoc(replyDoc.ref));
    });
    
    // Wait for all replies to be deleted
    await Promise.all(deletePromises);
    
    // Finally delete the post itself
    await deleteDoc(postRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

export const editPost = async (postId, newText, userId) => {
  try {
    // First check if the user is the author of the post
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    if (postData.authorId !== userId) {
      throw new Error('You can only edit your own posts');
    }
    
    // Update the post with new text and edit timestamp
    await updateDoc(postRef, {
      text: newText.trim(),
      editedAt: serverTimestamp(),
      isEdited: true
    });
    
    return true;
  } catch (error) {
    console.error('Error editing post:', error);
    throw error;
  }
};